import React, { useEffect, useState } from 'react';
import { Box, Chip, DialogActions, DialogContent, Typography } from '@mui/material';
import { Dialog, Button } from '@ui-components/ui-library';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'hooks/useTranslation';
import { ROUTE_CONSTANTS } from 'constants/routeConstant';
import { useFetchLatestReleaseNoteQuery } from '../../store/slices/releaseNotesApiSlice';
import { useFetchCurrentUserPermissionsQuery } from '../../store/slices/currentUserPermissionApiSlice';

const formatReleaseDate = (value: string): string => {
  if (!value) return '';

  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  const parsed = new Date(hasOffset ? value : `${value}Z`);

  if (Number.isNaN(parsed.getTime())) return '';

  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};


const ReleaseNotesPopup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Wait for the logged-in user / permissions call to resolve before fetching
  // release notes, so LOGGED_IN_USER is guaranteed to hit the backend first.
  // This hook shares the same (void) cache key as the other consumers, so it
  // only subscribes to the in-flight request rather than triggering a new one.
  const { isSuccess: isUserResolved } = useFetchCurrentUserPermissionsQuery();
  const { data: releaseNote } = useFetchLatestReleaseNoteQuery(undefined, {
    skip: !isUserResolved,
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (releaseNote) {
      setOpen(true);
    }
  }, [releaseNote]);

  const handleClose = () => setOpen(false);

  const handleViewAll = () => {
    setOpen(false);
    navigate(`/${ROUTE_CONSTANTS.RELEASE_NOTES}`);
  };

  if (!releaseNote) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ pt: 5, pb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Chip
            label={releaseNote.version}
            size="small"
            sx={{ backgroundColor: '#054e5a', color: '#fff', fontWeight: 600 }}
          />
          <Typography variant="caption" color="text.secondary">
            {formatReleaseDate(releaseNote.createdAt)}
          </Typography>
        </Box>

        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {releaseNote.title}
        </Typography>

        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
          {releaseNote.summary}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', pb: 3, px: 3, gap: 2 }}>
        <Button variant="outlined" onClick={handleViewAll}>
          {t('release_notes.view_all')}
        </Button>
        <Button variant="solid" color="primary" onClick={handleClose}>
          {t('release_notes.got_it')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseNotesPopup;
